import type { Matter, User } from "@/lib/types";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";

type TemplateUserIdentity = Pick<
  User,
  | "id"
  | "firmId"
  | "firstName"
  | "middleName"
  | "lastName"
  | "email"
  | "phoneNumber"
  | "enabled"
> &
  Partial<Pick<User, "barNumber">>;

export type TemplateUser = {
  isMatterUser: boolean;
  user: TemplateUserIdentity;
  roles: string[];
};

// Preserve matter order and roles, then append the rest of the firm's users.
export const buildTemplateUsers = (
  matter: Pick<Matter, "assignees" | "firmId">,
  firmUsers: TemplateUserIdentity[] = [],
): TemplateUser[] => {
  const users = new Map<string, TemplateUser>();
  matter.assignees
    .filter((assignee) => !assignee.dateTimeEnd && assignee.user.id)
    .forEach(({ user, role }) => {
      const existing = users.get(user.id);
      if (existing) {
        if (role.label && !existing.roles.includes(role.label)) {
          existing.roles.push(role.label);
        }
      } else {
        users.set(user.id, {
          isMatterUser: true,
          user,
          roles: role.label ? [role.label] : [],
        });
      }
    });
  firmUsers.forEach((user) => {
    if (
      user.enabled !== false &&
      user.id &&
      user.firmId === matter.firmId &&
      !users.has(user.id)
    ) {
      users.set(user.id, { isMatterUser: false, user, roles: [] });
    }
  });
  return [...users.values()];
};

export const getTemplateUserValues = (
  user: TemplateUserIdentity,
): Record<string, string> => ({
  userName: `${user.firstName} ${user.lastName}`.trim(),
  userFirstName: user.firstName || "",
  userMiddleName: user.middleName || "",
  userLastName: user.lastName || "",
  userEmail: user.email || "",
  userPhone: formatLocalOrIntlPhoneNumber({ number: user.phoneNumber }),
  userBarNumber: user.barNumber || "",
  userInitials:
    `${(user.firstName || "").trim().charAt(0)}${(user.lastName || "").trim().charAt(0)}`.toUpperCase(),
  userMiddleInitial: (user.middleName || "").trim().charAt(0),
});
